import React, { useState, useEffect } from 'react';
import { Modal, Header, Button, Icon, Dropdown, Popup } from 'semantic-ui-react';
import { Box, CircularProgress, Stack } from '@mui/material';
import { post } from '../util/api';
import _ from 'lodash';

// Create options array for dropdown
const createOptions = (maxCount = 300) => {
	const options = [];
	for (let i = 1; i <= maxCount; i++) {
		options.push({ key: `${i}`, text: `${i}`, value: i });
	}
	return options;
};

const PhoneSubscriptionUpdateModal = ({
	open,
	onClose,
	currentPhones,
	totalPhones,
	product,
	account_phone_price = 29,
	onSuccess,
	showPreview = true,
	onMessageUpdate,
}) => {
	const [loading, setLoading] = useState(false);
	const [quantityUpdate, setQuantityUpdate] = useState(1);
	const [options] = useState(createOptions());
	const [previewData, setPreviewData] = useState(null);
	const [previewModalOpen, setPreviewModalOpen] = useState(false);
	const [selectedSubscription, setSelectedSubscription] = useState(null);

	// Find the active phone subscription
	useEffect(() => {
		if (product && product.activeSubs && product.activeSubs.length > 0) {
			const phoneSubs = _.find(product.activeSubs, {
				data: {
					subscription_plan_id: product.settings.phone25 ? process.env.REACT_APP_PHONE_NEW_PLAN_ID : process.env.REACT_APP_PHONE_PLAN_ID,
				},
			});

			if (phoneSubs) {
				setSelectedSubscription({
					subscriptionId: phoneSubs.data.subscription_id,
					userId: phoneSubs.data.user_id,
					planId: phoneSubs.data.subscription_plan_id,
					oldQuantity: phoneSubs.data.quantity || phoneSubs.data.new_quantity,
					unitPrice: phoneSubs.data.unit_price
				});
			}
		}
	}, [product]);

	// If we have a subscription and this is just adding 1 phone, auto-set the quantity to 1
	useEffect(() => {
		if (selectedSubscription && !showPreview) {
			setQuantityUpdate(1);
		}
	}, [selectedSubscription, showPreview]);

	const handleChangeUpdate = (e, { value }) => {
		setQuantityUpdate(value);
	};

	const openPreview = async () => {
		if (!selectedSubscription) {
			// If no subscription, redirect to purchase screen
			openPurchaseFlow();
			return;
		}

		setLoading(true);

		try {
			// Calculate the new total quantity
			const newQuantity = showPreview ? parseInt(quantityUpdate) : parseInt(selectedSubscription.oldQuantity) + parseInt(quantityUpdate);

			const response = await post('/maytapi/checkpreview', {
				subscription_id: selectedSubscription.subscriptionId,
				quantity: newQuantity,
				selectedOldQuantity: selectedSubscription.oldQuantity,
			});

			setLoading(false);

			if (response.data.success) {
				setPreviewData(response.data.response);
				setPreviewModalOpen(true);
			} else {
				onMessageUpdate({
					message: response.data.message.message || 'Error getting preview data',
					color: 'red',
				});
				onClose();
			}
		} catch (err) {
			console.error('Error getting preview data:', err);
			setLoading(false);
			onMessageUpdate({
				message: 'Error communicating with the server',
				color: 'red',
			});
			onClose();
		}
	};

	const updateSubscription = async () => {
		if (!selectedSubscription || !previewData) return;

		setLoading(true);

		try {
			// Calculate the new total quantity
			const newQuantity = showPreview ? parseInt(quantityUpdate) : parseInt(selectedSubscription.oldQuantity) + parseInt(quantityUpdate);

			const response = await post('/maytapi/updateSubscription', {
				quantity: newQuantity,
				pid: product.id,
				subscription_id: selectedSubscription.subscriptionId,
				old_quantity: selectedSubscription.oldQuantity,
				plan_id: selectedSubscription.planId,
				user_id: selectedSubscription.userId,
				next_payment_amount: previewData.next_payment && previewData.next_payment.amount,
			});

			setLoading(false);

			if (response.data.success) {
				onMessageUpdate({
					message: 'Update process is successful!',
					color: 'green',
				});
				onSuccess();
			} else {
				onMessageUpdate({
					message: response.data.message.message || 'Update process failed',
					color: 'red',
				});
			}

			closePreviewModal();
			onClose();
		} catch (err) {
			console.error('Error updating subscription:', err);
			setLoading(false);
			onMessageUpdate({
				message: 'Error communicating with the server',
				color: 'red',
			});
			closePreviewModal();
			onClose();
		}
	};

	const openPurchaseFlow = async () => {
		setLoading(true);

		try {
			const link = await post('/maytapi/custompaddlelink', {
				plan_id: product.settings.phone25 ? process.env.REACT_APP_PHONE_NEW_PLAN_ID : process.env.REACT_APP_PHONE_PLAN_ID,
				isVat: false,
				pid: product.id,
				email: product.email || '',
			});

			window.Paddle.Checkout.open({
				override: link.data.response.url,
				successCallback: (data) => {
					post('/maytapi/checkSubscription', {
						checkout_id: data.checkout.id,
						plan_id: data.product.id,
						user_id: data.user.id,
						pid: product.id,
						quantity: data.product.quantity,
						email: data.user.email,
						extra: data,
					})
						.then((resp) => {
							if (resp.data.success) {
								onMessageUpdate({
									message: 'Purchase is successful!',
									color: 'green',
								});
								onSuccess();
							} else {
								onMessageUpdate({
									message: (resp.data.message && resp.data.message.message) || resp.data.message,
									color: 'red',
								});
							}
						})
						.catch((err) => {
							console.error(err);
						});
				},
				closeCallback: () => {
					onMessageUpdate({
						message: 'Your process has been cancelled.',
						color: 'red',
					});
				},
			});
		} catch (err) {
			console.error('Error initiating purchase:', err);
			onMessageUpdate({
				message: 'Error communicating with the server',
				color: 'red',
			});
		}

		setLoading(false);
		onClose();
	};

	const closePreviewModal = () => {
		setPreviewModalOpen(false);
	};

	return (
		<>
			<Modal open={open} onClose={onClose} style={{ width: 500 }}>
				<Header icon="redo" content={selectedSubscription ? 'Update Phone Limit' : 'Add New Phone'} />
				<Modal.Content style={{ textAlign: 'center' }}>
					<p>
						{selectedSubscription
							? showPreview
								? 'Please select how many total phones you want in your subscription. Your current subscription limit is ' +
								  selectedSubscription.oldQuantity +
								  ' phones. The new amount will be applied to your next billing cycle.'
								: 'You are adding one more phone to your current subscription of ' +
								  selectedSubscription.oldQuantity +
								  ' phones. This will increase your monthly cost by $' +
								  parseFloat(selectedSubscription.unitPrice) +
								  '.'
							: 'You need to purchase a subscription to add phones:'}
					</p>
				</Modal.Content>

				{selectedSubscription && showPreview && (
					<Modal.Content style={{ display: 'flex' }}>
						<Modal.Content style={{ width: 250, textAlign: 'center' }}>
							<Dropdown
								options={options}
								placeholder="Choose Quantity"
								selection
								value={quantityUpdate}
								defaultValue={selectedSubscription.oldQuantity || 1}
								onChange={handleChangeUpdate}
							/>
						</Modal.Content>
						<Modal.Content style={{ width: 250, textAlign: 'center', margin: 'auto' }}>
							<p>Total amount will be: ${quantityUpdate * parseFloat(selectedSubscription.unitPrice)}/month</p>
						</Modal.Content>
					</Modal.Content>
				)}

				{!selectedSubscription && (
					<Modal.Content style={{ textAlign: 'center' }}>
						<p>Phone subscription: ${account_phone_price}/month per phone</p>
					</Modal.Content>
				)}

				<Modal.Actions style={{ textAlign: 'center' }}>
					<Stack spacing={2} direction="row">
						<Button color="red" onClick={onClose} disabled={loading}>
							<Icon name="remove" /> Cancel
						</Button>

						<Button color="green" onClick={selectedSubscription ? openPreview : openPurchaseFlow} disabled={loading}>
							{loading ? (
								<Box sx={{ display: 'flex', alignItems: 'center' }}>
									<CircularProgress size={24} sx={{ color: 'white', mr: 1 }} />
									Processing...
								</Box>
							) : (
								<>
									<Icon name="checkmark" /> {selectedSubscription ? 'Continue' : 'Purchase'}
								</>
							)}
						</Button>
					</Stack>
				</Modal.Actions>
			</Modal>

			{/* Preview Modal */}
			<Modal open={previewModalOpen} onClose={closePreviewModal} style={{ maxWidth: '700px' }}>
				<Header icon="exchange" content="Update Subscription" />
				<Modal.Content>
					<div style={{ display: 'flex' }}>
						{previewData && previewData.immediate_payment ? (
							<div style={{ margin: 'auto' }}>
								<h3>Immediate Payment:</h3>
								<p>Amount: {previewData.immediate_payment.amount} $</p>
								<p>Date: {previewData.immediate_payment.date}</p>
							</div>
						) : null}
						<div style={{ margin: 'auto' }}>
							<h3>Next Payment:</h3>
							<p>Amount: {previewData && previewData.next_payment.amount} $</p>
							<p>Date: {previewData && previewData.next_payment.date}</p>
						</div>
					</div>
				</Modal.Content>
				<Modal.Actions style={{ textAlign: 'left' }}>
					<div>
						<h3 style={{ marginBottom: 'auto', marginTop: 'auto' }}>Are you sure you want to update your phone subscription?</h3>
						<Stack spacing={2} direction="row" style={{ marginTop: '10px' }}>
							<Button color="green" onClick={updateSubscription} disabled={loading}>
								{loading ? (
									<Box sx={{ display: 'flex', alignItems: 'center' }}>
										<CircularProgress size={24} sx={{ color: 'white', mr: 1 }} />
										Processing...
									</Box>
								) : (
									<>
										<Icon name="checkmark" /> Yes
									</>
								)}
							</Button>
							<Button color="red" onClick={closePreviewModal} disabled={loading}>
								<Icon name="remove" /> No
							</Button>
						</Stack>
					</div>
				</Modal.Actions>
			</Modal>
		</>
	);
};

export default PhoneSubscriptionUpdateModal;
